import { useRouter } from 'next/router'
import { useLocale } from 'next-intl'

import { indexes } from '@mab-common/utils'

import type {
  MsearchRequest,
  QueryDslQueryContainer,
} from '@elastic/elasticsearch/lib/api/types'

import useSWRInfinite from 'swr/infinite'

import {
  buildElasticResultSourceFields,
  buildElasticSortFields,
  buildElasticToHits,
  buildMappedElasticHits,
} from '~/services/elastic/builder'
import EsIndexTypes = indexes.EsIndexTypes
import RecordTypes = indexes.RecordTypes
import { elasticClientFetcher } from '../elastic/client'

export default function useArticles(
  articlesTypeSlug?: string | boolean,
  fallbackData?: any,
  pageSize = 60,
  idFilterList?: string[]
) {
  const { query } = useRouter()
  const locale = useLocale()

  const getFilters = (pageIndex: number, previousPageData: any) => {
    if (previousPageData && !previousPageData.length) return null // reached the end
    const pageFrom = pageIndex * pageSize
    const filterQueryParam = query?.filter ? query?.filter : undefined
    const artistQueryParam = query?.artist ? query?.artist : undefined
    const authorQueryParam = query?.author ? query?.author : undefined

    const mustNotQueries: QueryDslQueryContainer[] = (
      idFilterList && idFilterList.length > 0 ? idFilterList : []
    ).map(id => {
      return {
        term: {
          objectID: id,
        },
      }
    })

    const filterQueries: QueryDslQueryContainer[] = [
      {
        term: { type: 'article' },
      },
    ]

    // Add language filtering based on locale
    if (locale === 'en') {
      // For English, include articles where lang is 'en' OR lang is not defined
      filterQueries.push({
        bool: {
          should: [
            { term: { lang: 'en' } },
            { bool: { must_not: { exists: { field: 'lang' } } } },
          ],
          minimum_should_match: 1,
        },
      })
    } else {
      // For other locales, only include articles where lang matches the locale
      filterQueries.push({
        term: { lang: locale },
      })
    }

    if (artistQueryParam) {
      filterQueries.push({
        term: { 'articleTypeSlug.keyword': artistQueryParam },
      })
    }
    if (authorQueryParam) {
      filterQueries.push({
        term: { 'author.keyword': authorQueryParam },
      })
    }

    const shouldQueries: QueryDslQueryContainer[] = []

    if (articlesTypeSlug) {
      shouldQueries.push({
        term: { 'articleTypeSlug.keyword': articlesTypeSlug },
      })
      shouldQueries.push({
        term: { 'secondaryCategories.slug.keyword': articlesTypeSlug },
      })
    }

    if (filterQueryParam) {
      shouldQueries.push({
        term: { 'articleTypeSlug.keyword': filterQueryParam },
      })
      shouldQueries.push({
        term: { 'secondaryCategories.slug.keyword': filterQueryParam },
      })
    }

    if (articlesTypeSlug && filterQueryParam) {
      filterQueries.push({
        term: { 'articleTypeSlug.keyword': articlesTypeSlug },
      })
    }

    let minShouldMatch = undefined

    if (shouldQueries?.length) {
      minShouldMatch = 1
    }

    // why am I doing this? to get type safety directly from elastic types without 'as'
    return ((): MsearchRequest => {
      return {
        index: EsIndexTypes.ARTICLES,
        searches: [
          {},
          {
            from: pageFrom,
            size: pageSize,
            sort: buildElasticSortFields(RecordTypes.ARTICLES_INDEX),
            _source: buildElasticResultSourceFields(RecordTypes.ARTICLES),
            query: {
              bool: {
                must_not: mustNotQueries?.length ? mustNotQueries : undefined,
                filter: filterQueries,
                should: shouldQueries?.length ? shouldQueries : undefined,
                minimum_should_match: minShouldMatch,
              },
            },
          },
        ],
      }
    })()
  }

  const { data, error, mutate, size, setSize, isValidating } = useSWRInfinite(
    getFilters,
    async (props: MsearchRequest) => {
      const d = await elasticClientFetcher(props)
      const elasticResult = buildMappedElasticHits(
        buildElasticToHits(RecordTypes.ARTICLES, d?.[0])
      )

      return elasticResult
    },
    {
      revalidateOnFocus: false,
      fallbackData: fallbackData,
      revalidateFirstPage: false,
    }
  )

  const processedData = data?.flatMap(d => d?.[0]).filter(Boolean)

  return {
    data: processedData,
    error,
    mutate,
    size,
    setSize,
    isValidating,
  }
}
